import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from "react";
import {
  Datepicker,
  Eventcalendar,
  MbscCalendarEvent,
  MbscEventClickEvent
} from "@mobiscroll/react";
import moment, { max } from "moment-timezone";
import { Stack, Divider, useColorScheme } from "@mui/material";
import { useDebouncedCallback } from "use-debounce";
import { first, last, nth } from "lodash-es";

import { MeetingAutobookInfo } from "../components/Meeting/MeetingAutobookInfo";
import { useSchedulerStore } from "../state/scheduler.store";
import {
  getSlotCountMobiscrollDatePickerLabels,
  getUnavailableSlots,
  isValidTimeSlot
} from "../utils/slots";
import "../../src/assets/mobiscroll.css";
import DatePickerSkeleton from "../components/Skeleton/DatePickerSkeleton";
import { DateNavigationHeader } from "../components/Header/DateNavigationHeader";
import { disableDateNavButton, navigateWeek } from "../utils/dateNavigation";
import { AltChatUI } from "../components/Oliver/AltChatUI";
import { PoweredByBadge } from "../components/Branding/PoweredBy";
import { useAutobookDatePicker } from "../hooks/useDatePicker";
import { useTranslation } from "react-i18next";
import { getMobiscrollLocale } from "../i18n/mobiscroll";
import { useScreenSize } from "../hooks/useScreenSize";
interface IWeekViewProps {
  currentlySelectedSlot: string | null;
  previouslySelectedTime: string | null;
  handleTimezoneChange: (tz: string) => void;
}

export const WeekView: React.FC<IWeekViewProps> = ({
  currentlySelectedSlot,
  previouslySelectedTime,
  handleTimezoneChange
}) => {
  const { t, i18n } = useTranslation();
  const calInst = useRef<Eventcalendar>(null);
  const [myEvents, setMyEvents] = useState<MbscCalendarEvent[]>([]);
  const { isLargeScreen } = useScreenSize();
  const { mode: themeVariant } = useColorScheme();
  const { minDate, maxDate, selectedDate } = useAutobookDatePicker();

  const {
    meetingLink,
    host,
    sessionData,
    actions,
    clientId,
    ui,
    clientLogo,
    loadingAvailability
  } = useSchedulerStore();

  const getSelectedTimePos = () => {
    const targetNode = document.getElementsByClassName(
      "mbsc-schedule-cursor-time mbsc-schedule-cursor-time-x"
    )?.[0];

    const cellDomTree = document.querySelectorAll(":hover");
    const parentDom = nth(cellDomTree, -4);
    const dayDom = nth(cellDomTree, -3);
    if (dayDom === undefined) return;
    const dayDomIndex = Array.from(parentDom?.children ?? []).indexOf(dayDom);

    if (
      targetNode?.innerHTML &&
      dayDom &&
      parentDom?.children &&
      parentDom?.children?.length > 0 &&
      last(cellDomTree)?.className.includes("mbsc-schedule-item")
    ) {
      const hoverTime = targetNode.innerHTML;

      const [hour, minutes] = hoverTime
        .replace("AM", "")
        .replace("PM", "")
        .replace(" ", "")
        .split(":");

      const hour12Format = Number.parseInt(hour);
      const hasAMMarker = hoverTime.includes("AM");
      const isAM = hasAMMarker;

      const hoverDate = moment(calInst.current?._selectedDateTime)
        .startOf("week")
        .add(dayDomIndex, "day")
        .startOf("day");

      const start = hoverDate
        .startOf("day")
        .add(isAM ? hour12Format % 12 : (hour12Format % 12) + 12, "hours")
        .add(Number.parseInt(minutes), "minutes");

      const eventStart = start.toDate();
      const eventEnd = start
        .add(meetingLink?.availability?.duration, "minutes")
        .toDate();

      const invalidSlots = getAllInvalidSlots();

      const isValid = isValidTimeSlot(eventStart, eventEnd, invalidSlots ?? []);

      const newEvent = {
        id: isValid ? "available" : "unavailable",
        title: isValid
          ? t("ROOSTER.PUBLIC-APP.BOOK_THIS_SLOT", {
              defaultValue: "Book this slot"
            })
          : t("ROOSTER.PUBLIC-APP.UNAVAILABLE", {
              defaultValue: "Unavailable"
            }),
        description: "",
        start: eventStart,
        end: eventEnd,
        allDay: false,
        color: isValid
          ? themeVariant === "light"
            ? "#2e4874"
            : "#6f6f6f"
          : "#b1271b"
      };

      setMyEvents([newEvent]);
    }
  };

  const debounced = useDebouncedCallback(getSelectedTimePos, 100);

  const handleClickOpen = (slot: {
    date: Date;
    hour: number;
    minute: number;
  }) => {
    actions.setSessionData({
      ...sessionData,
      selectedSlot: {
        hour: slot.hour,
        minute: slot.minute,
        date: moment(slot.date).valueOf()
      }
    });
    actions.setUiState({ ...ui, currentStep: "form", currentView: "week" });
  };

  const getAllInvalidSlots = () => {
    if (
      meetingLink?.availability?.range &&
      meetingLink?.availability?.autobookIntervalFreq &&
      sessionData?.computedAvailability?.length &&
      sessionData?.selectedTimezone
    ) {
      const result = getUnavailableSlots(
        sessionData?.computedAvailability,
        meetingLink?.availability?.duration,
        moment().startOf("week").startOf("day").format(),
        moment(last(meetingLink?.availability?.range)?.rangeEnd)
          .endOf("week")
          .endOf("day")
          .add(1, "day")
          .format()
      );
      return result;
    }
  };

  const handleDatepickerChange = (event: { value: Date | null }) => {
    if (event.value?.valueOf()) {
      calInst.current?.navigate(event.value, true);
      actions.setSessionData({
        ...sessionData,
        prevSelectedSlot: sessionData?.prevSelectedSlot,
        selectedSlot: {
          date: moment(event.value.valueOf()).valueOf(),
          hour: null,
          minute: null
        }
      });
    }
  };

  const handleEventClick = (event: MbscEventClickEvent) => {
    if (event.event.id === "available") {
      handleClickOpen({
        date: event.date,
        hour: moment(event.event.start).hour(),
        minute: moment(event.event.start).minute()
      });
    }
  };

  const handlePreviousWeek = () =>
    navigateWeek("previous", sessionData, actions);
  const handleNextWeek = () => navigateWeek("next", sessionData, actions);

  const disableDateNavPrevButton = useMemo(
    () => disableDateNavButton("previous", sessionData),
    [sessionData?.selectedSlot, sessionData?.computedAvailability]
  );

  const disableDateNavNextButton = useMemo(
    () => disableDateNavButton("next", sessionData),
    [sessionData?.selectedSlot, sessionData?.computedAvailability]
  );

  useEffect(() => {
    /**
     * Sets up a timeout to navigate the calendar instance to a specific date
     * after a 500ms delay.
     *
     * @remarks
     * This timeout ensures the calendar instance is available and a date is selected
     * before attempting navigation.
     */

    const intervalId = setTimeout(() => {
      if (calInst?.current && sessionData?.selectedSlot?.date) {
        const date = moment(sessionData.selectedSlot.date).toDate();
        if (date && calInst.current?.navigate) {
          calInst.current.navigate(date, true);
        }
      }
    }, 500);

    return () => clearTimeout(intervalId);
  }, []);

  useEffect(() => {
    if (selectedDate) {
      calInst.current?.navigate(moment(selectedDate), true);
    }
  }, [selectedDate]);

  return (
    <Stack
      direction="row"
      justifyContent="center"
      alignItems="flex-start"
      minWidth="100%"
      sx={{
        height: "100dvh",
        overflow: "hidden"
      }}
      divider={<Divider orientation="vertical" flexItem />}
    >
      <Stack
        direction="row"
        divider={<Divider orientation="vertical" flexItem />}
        minWidth="100%"
      >
        {/* Info Col */}
        <Stack
          pl={3}
          pr={1}
          pt={1.5}
          justifyContent="space-between"
          flex={1}
          tabIndex={0}
          role="region"
          aria-label={t("ROOSTER.PUBLIC-APP.MEETING_INFO_REGION", {
            defaultValue: "Meeting information"
          })}
          sx={{
            minWidth: "380px",
            height: "100dvh",
            overflowY: "auto",
            overflowX: "hidden",
            paddingRight: "8px !important",
            "&::-webkit-scrollbar": {
              width: "16px",
              background: "transparent",
              borderRadius: "20px" // Added for rounded track
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "transparent",
              borderLeft: "10px solid transparent",
              backgroundClip: "padding-box",
              borderRadius: "20px" // Increased for rounder edges
            },
            "&:hover": {
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#888",
                borderLeft: "10px solid transparent",
                backgroundClip: "padding-box"
              },
              "&::-webkit-scrollbar-thumb:hover": {
                backgroundColor: "#555"
              }
            }
          }}
        >
          <Stack pb={1}>
            <MeetingAutobookInfo
              isRoosterLink={meetingLink?.type === "rooster_links"}
              clientLogo={clientLogo}
              uiStep={ui?.currentStep ?? "loading"}
              meetingDuration={meetingLink?.availability?.duration ?? null}
              host={host ?? null}
              selectedTimezone={sessionData?.selectedTimezone ?? null}
              participants={meetingLink?.interviewers ?? []}
              timeSlots={{
                previouslySelectedTime,
                currentlySelectedSlot
              }}
              handleTimezoneChange={handleTimezoneChange}
            />
          </Stack>

          <Stack px={-3}>
            {clientId &&
            sessionData?.computedAvailability?.length &&
            !loadingAvailability ? (
              <Datepicker
                selectMultiple={false}
                onChange={handleDatepickerChange}
                min={minDate}
                max={maxDate}
                display="inline"
                calendarType="month"
                showOuterDays={false}
                labels={getSlotCountMobiscrollDatePickerLabels(
                  sessionData?.computedAvailability,
                  t
                )}
                valid={sessionData?.computedAvailability?.map((a) => a.date)}
                value={selectedDate}
                themeVariant={themeVariant === "light" ? "light" : "dark"}
                locale={getMobiscrollLocale(t, i18n.language)}
              />
            ) : (
              <DatePickerSkeleton rows={5} columns={7} />
            )}
          </Stack>
        </Stack>

        {/* Scheduler */}
        <Stack
          flex={4}
          // minWidth="50%"
          maxWidth="100%"
          divider={<Divider orientation="horizontal" flexItem />}
          maxHeight="100dvh"
          tabIndex={0}
          role="region"
          aria-label={t("ROOSTER.PUBLIC-APP.CALENDAR_REGION", {
            defaultValue: "Calendar"
          })}
          sx={{
            opacity: loadingAvailability ? 0.3 : 1,
            pointerEvents: loadingAvailability ? "none" : "auto",
            overflowY: "auto",
            overflowX: "hidden",
            "&::-webkit-scrollbar": {
              width: "2px" // Changed from default to 2px
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#888",
              borderRadius: "10px"
            },
            "&::-webkit-scrollbar-thumb:hover": {
              backgroundColor: "#555"
            }
          }}
        >
          <DateNavigationHeader
            selectedDate={sessionData?.selectedSlot?.date ?? moment().valueOf()}
            isLoading={
              !clientId ||
              loadingAvailability ||
              !sessionData?.computedAvailability?.length
            }
            disablePrev={disableDateNavPrevButton}
            disableNext={disableDateNavNextButton}
            onPreviousWeek={handlePreviousWeek}
            onNextWeek={handleNextWeek}
            showNavigation={Boolean(
              clientId &&
                sessionData?.computedAvailability &&
                sessionData?.computedAvailability?.length > 0
            )}
          />
          <Stack
            display="inline"
            direction="row"
            spacing={2}
            overflow="auto"
            tabIndex={0}
            role="region"
            aria-label={t("ROOSTER.PUBLIC-APP.TIME_SLOTS_REGION", {
              defaultValue: "Available time slots"
            })}
            onMouseMove={debounced}
          >
            <Eventcalendar
              locale={getMobiscrollLocale(t, i18n.language)}
              onEventClick={handleEventClick}
              data={myEvents}
              ref={calInst}
              clickToCreate={true}
              dragInTime
              dragToMove
              dragTimeStep={5}
              themeVariant={themeVariant === "light" ? "light" : "dark"}
              showControls={false}
              min={minDate}
              max={moment(maxDate).add(1, "day")}
              view={{
                schedule: {
                  allDay: false,
                  type: "week",
                  startDay: moment(sessionData?.selectedSlot?.date)
                    .startOf("week")
                    .get("day"),
                  endDay: moment(sessionData?.selectedSlot?.date)
                    .endOf("week")
                    .get("day"),
                  startTime: "00:00",
                  endTime: "00:00",
                  timeCellStep: 60,
                  timeLabelStep: 60,
                  currentTimeIndicator: false
                }
              }}
              invalid={[
                ...(getAllInvalidSlots()?.map((i) => ({
                  start: i.start,
                  end: i.end,
                  title: ""
                })) ?? [])
              ]}
            />
          </Stack>
        </Stack>
        <AltChatUI view="weeklyView" showChatIcon={!isLargeScreen} />
        <PoweredByBadge show hideOnHover />
      </Stack>
    </Stack>
  );
};
